import { Text, Icon, Colors, Checkbox, Pills, Typography, Heading } from '@amelio/ui-library';
import { AnimatePresence, motion } from 'motion/react';
import { useMemo, useState, useEffect } from 'react';

import type { CompanyValue } from '@/types/company-values';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';

import { useGetCompanyValues } from '@/api/company-values';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { resolveCompanyValueIconBackground } from '@/utils/companyValueIconBackground';
import { getIconSvg } from '@/utils/iconSvgs';
import { getTextByLanguage } from '@/utils/localization';
import { createEmptyQuestion } from '@/utils/question';

interface CompanyValueAssignmentProps {
    question?: ReviewQuestion;
    onQuestionChange: (question: ReviewQuestion) => void;
}

export const CompanyValueAssignment = ({
    question,
    onQuestionChange,
}: CompanyValueAssignmentProps) => {
    const { t } = useAmelioTranslations();
    const defaultLanguage = useSelectedAppLanguage();
    const emptyQuestion = useMemo(() => createEmptyQuestion(defaultLanguage), [defaultLanguage]);
    const [isSectionEnabled, setIsSectionEnabled] = useState(false);

    // Fetch company values for the cards
    const { data: companyValues = [] } = useGetCompanyValues();
    const activeCompanyValues = companyValues.filter((cv) => cv.status === 'Active');

    // Initialize toggle state based on existing companyValueId
    useEffect(() => {
        setIsSectionEnabled(!!question?.companyValueId);
    }, [question?.companyValueId]);

    const handleToggleChange = (value: boolean) => {
        setIsSectionEnabled(value);
        if (!value) {
            // Clear company value when toggled off
            const currentQuestion = question || emptyQuestion;
            onQuestionChange({
                ...currentQuestion,
                companyValueId: null,
            });
        }
    };

    const handleCompanyValueSelect = (companyValueId: string) => {
        // Always update even if question is undefined
        const currentQuestion = question || emptyQuestion;

        // Toggle selection: deselect if clicking the same card
        const newCompanyValueId =
            currentQuestion.companyValueId === companyValueId ? null : companyValueId;

        // Enable section when selecting a value
        if (newCompanyValueId !== null) {
            setIsSectionEnabled(true);
        }

        onQuestionChange({
            ...currentQuestion,
            companyValueId: newCompanyValueId,
            // Company-value questions are scored against the selected value and do not
            // support free-form answers.
            hasOtherOption: false,
        });
    };

    return (
        <div className={'mt-8'}>
            <div
                role={'button'}
                tabIndex={0}
                onClick={() => handleToggleChange(!isSectionEnabled)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleToggleChange(!isSectionEnabled);
                    }
                }}
                className={`group flex cursor-pointer items-center justify-between rounded-2xl border-2 p-6 shadow-sm transition-all ${
                    isSectionEnabled
                        ? 'border-primary-500 bg-linear-to-r from-[rgba(13,211,188,0.15)] via-[rgb(250,245,255)] to-[rgba(13,211,188,0.1)]'
                        : 'border-base-100 hover:border-primary bg-white hover:shadow-md'
                }`}
            >
                <div className={'flex items-center gap-3'}>
                    <div
                        className={`${isSectionEnabled ? 'bg-primary-500' : 'bg-base-50'} flex aspect-square h-14 w-14 shrink-0 items-center justify-center rounded-xl`}
                        aria-hidden={'true'}
                    >
                        <Icon
                            name={'design-layer'}
                            size={32}
                            colorClassName={`${isSectionEnabled ? 'text-white' : 'text-black group-hover:text-primary'}`}
                            ariaLabel={t(
                                'evaluation360Questionnaire.questionForm.companyValue.assignToEnterpriseValue',
                            )}
                        />
                    </div>
                    <div className={'flex flex-col gap-1'}>
                        <div
                            className={`flex items-center gap-2 transition-colors ${isSectionEnabled ? 'text-primary' : 'text-base-900 group-hover:text-primary'}`}
                        >
                            <Typography
                                weight={'bold'}
                                variant={'h6'}
                                color={'inherit'}
                                className={'text-[16px] font-semibold transition-colors'}
                            >
                                {t(
                                    'evaluation360Questionnaire.questionForm.companyValue.assignToEnterpriseValue',
                                )}
                            </Typography>
                            {isSectionEnabled && (
                                <Pills
                                    variant={'primary'}
                                    size={'sm'}
                                >
                                    {t('common.active')}
                                </Pills>
                            )}
                        </div>
                        <Typography className={'text-sm text-slate-500'}>
                            {t(
                                'evaluation360Questionnaire.questionForm.companyValue.measuringAdherenceToValues',
                            )}
                        </Typography>
                    </div>
                </div>
                <Checkbox
                    checked={isSectionEnabled}
                    onChange={(checked) => handleToggleChange(checked)}
                    className={'cursor-pointer'}
                />
            </div>

            <AnimatePresence>
                {isSectionEnabled && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className={'mt-6 p-6'}
                    >
                        <div className={'mb-6 flex items-center gap-4'}>
                            <span className={'bg-primary-500 h-8 w-2 rounded-full'} />
                            <Heading
                                level={3}
                                weight={'bold'}
                                className={'text-body'}
                            >
                                {t(
                                    'evaluation360Questionnaire.questionForm.companyValue.selectValue',
                                )}
                            </Heading>
                        </div>

                        {activeCompanyValues.length === 0 ? (
                            <Text
                                className={'text-sm text-gray-500'}
                                size={'small'}
                            >
                                {t(
                                    'evaluation360Questionnaire.questionForm.companyValue.noActiveValuesAvailable',
                                )}
                            </Text>
                        ) : (
                            <div className={'grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2'}>
                                {activeCompanyValues.map((companyValue: CompanyValue) => {
                                    const isSelected = question?.companyValueId === companyValue.id;
                                    return (
                                        <button
                                            key={companyValue.id}
                                            type={'button'}
                                            onClick={() =>
                                                handleCompanyValueSelect(companyValue.id)
                                            }
                                            className={`group border-base-100 relative flex w-full flex-col items-center justify-center gap-3 overflow-hidden rounded-2xl border-2 bg-white px-6 py-7 text-center shadow-md transition-all duration-300 hover:scale-[1.02] hover:shadow-xl ${
                                                isSelected
                                                    ? 'border-primary-500'
                                                    : 'border-base-100'
                                            }`}
                                            style={{ backdropFilter: 'blur(8px)' }}
                                        >
                                            {isSelected && (
                                                <div
                                                    className={`bg-primary-500 absolute top-3 right-3 z-10 flex h-6 w-6 items-center justify-center rounded-full`}
                                                >
                                                    <Icon
                                                        name={'essential-checkmark'}
                                                        size={'sm'}
                                                        color={Colors.WHITE}
                                                    />
                                                </div>
                                            )}
                                            <div
                                                className={`relative flex h-16 w-16 items-center justify-center rounded-2xl shadow-lg transition-all duration-300 group-hover:scale-105`}
                                                style={{
                                                    background: resolveCompanyValueIconBackground(
                                                        companyValue.icon,
                                                        companyValue.iconBackground,
                                                    ),
                                                }}
                                            >
                                                {getIconSvg(companyValue.icon || 'heart', 28)}
                                            </div>

                                            <Typography
                                                color={isSelected ? Colors.PRIMARY : Colors.BASE}
                                                className={`text-body relative text-center font-bold`}
                                            >
                                                {getTextByLanguage(
                                                    companyValue.name,
                                                    defaultLanguage,
                                                )}
                                            </Typography>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
